  import React, { useEffect, useState, useCallback } from "react";
  import { useNavigate } from "react-router-dom";
  import Footer from "./Footer";
  import "./Cart.css";
  import {BASE_URL} from "./Constants"

  const Cart = () => {
    const [cartItems, setCartItems] = useState([]);
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // ---------------- Fetch logged-in user ----------------
    useEffect(() => {
      const fetchUser = async () => {
        try {
          const res = await fetch(`${BASE_URL}/get_user_details`, {
            method: "GET",
            credentials: "include",
          });

          if (!res.ok) throw new Error("Not logged in");

          const data = await res.json();
          setUser(data); // { user_id, email }
        } catch (err) {
          console.error("User not logged in:", err.message);
          navigate("/checkout/login");
        }
        finally{
          setLoading(false);
        }
      };

      fetchUser();
    }, [navigate]);

    // ---------------- Fetch cart items ----------------
    const fetchCartItems = useCallback(async () => {
      if (!user) return;

      try {
        const res = await fetch(
          `${BASE_URL}/cart/fetch_cart_details?user_id=${user.user_id}`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || "Failed to fetch cart");
        }

        const data = await res.json();
        // ensure stable sorting
        const sortedItems = (data.items || []).sort(
          (a, b) => (a.cart_item_id || 0) - (b.cart_item_id || 0)
        );
        setCartItems(sortedItems);
      } catch (err) {
        console.error("Error fetching cart:", err);
        setCartItems([]);
      }
    }, [user]);

    useEffect(() => {
      fetchCartItems();
    }, [fetchCartItems]);

    // ---------------- Quantity change ----------------
    const handleQuantityChange = async (cartItemId, delta) => {
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.cart_item_id === cartItemId
            ? { ...item, quantity: Math.max(1, item.quantity + delta) }
            : item
        )
      );

      try {
        const item = cartItems.find((i) => i.cart_item_id === cartItemId);
        if (!item) return;

        const newQuantity = Math.max(1, item.quantity + delta);

        const res = await fetch(
          `${BASE_URL}/cart_items/update_cart_item/${cartItemId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ quantity: newQuantity }),
          }
        );

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.error || "Failed to update quantity");
        }

        window.dispatchEvent(new CustomEvent("cartUpdated", { detail: { user_id: user.user_id } }));
      } catch (err) {
        console.error("Error updating quantity:", err);
      }
    };

    // ---------------- Remove item ----------------
    const handleRemoveItem = async (cartItemId) => {
      try {
        const res = await fetch(
          `${BASE_URL}/cart_items/delete_cart_item/${cartItemId}`,
          {
            method: "DELETE",
            credentials: "include",
          }
        );

        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.error || "Failed to delete item");
        }

        // remove from UI instantly
        setCartItems((prev) =>
          prev.filter((item) => item.cart_item_id !== cartItemId)
        );

        window.dispatchEvent(new Event("cartUpdated"));
      } catch (err) {
        console.error("Error removing item:", err);
      }
    };

    // ---------------- Subtotal ----------------
    const calculateSubtotal = () =>
      cartItems.reduce((total, item) => {
        const price =
          typeof item.price === "string"
            ? parseFloat(item.price.replace(/[^\d.-]/g, ""))
            : typeof item.price === "number"
            ? item.price
            : 0;
        return total + price * item.quantity;
      }, 0);

      // block ui until user is loging or not
      if (loading) return <p>Loading...</p>;

      if (!user) return null;

    return (
      <>
        <div className="cart-container">
          <h2>Your Cart</h2>
          <table className="cart-table">
            <thead>
              <tr>
                <th>Tittle</th>
                <th>Product</th>
                <th>Unit Price</th>
                <th>Quantity</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cartItems.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center" }}>
                    Your cart is empty
                  </td>
                </tr>
              ) : (
                cartItems.map((item, index) => {
                  const price =
                    typeof item.price === "string"
                      ? parseFloat(item.price.replace(/[^\d.-]/g, ""))
                      : typeof item.price === "number"
                      ? item.price
                      : 0;
                  return (
                    <tr
                      key={`${item.cart_item_id || item.item_id || index}`}
                      className="cart-row"
                    >
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/Product/${item.item_id}`)}
                      >
                        {item.image_id ? (
                          <img
                            src={
                              item.image_id
                                ? `${BASE_URL}/cart/download_image/${item.image_id}`
                                : "/assets/default.jpg"
                              }
                              alt={item.name}
                              style={{ width: "60px", height: "60px", objectFit: "cover" }}
                          />
                        ) : (
                          "No image"
                        )}
                      </td>
                      <td 
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/Product/${item.item_id}`)}
                      >
                        {item.name}
                      </td>
                      <td>₹ {price.toFixed(2)}</td>
                      <td>
                        <button
                          onClick={() =>
                            handleQuantityChange(item.cart_item_id, -1)
                          }
                        >
                          -
                        </button>
                        <span style={{ margin: "0 8px" }}>{item.quantity}</span>
                        <button
                          onClick={() =>
                            handleQuantityChange(item.cart_item_id, 1)
                          }
                        >
                          +
                        </button>
                      </td>
                      <td>
                        <button
                          className="cart-remove-btn"
                          onClick={() => handleRemoveItem(item.cart_item_id)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {cartItems.length > 0 && (
            <div className="cart-summary-box">
              <h2>Cart Total</h2>
              <div className="summary-table">
                <div className="summary-row">
                  <span>SubTotal</span>
                  <span>₹ {calculateSubtotal().toFixed(2)}</span>
                </div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>
                    <strong>₹ {calculateSubtotal().toFixed(2)}</strong>{" "}
                    <span className="excl">(Excl.GST)</span>
                  </span>
                </div>
              </div>
              <div className="note">
              <strong>Note:</strong> Any applicable taxes and shipping fees will be added at checkout. You’ll see the total amount before confirming your order.            </div>
              <button
                className="checkout-btn"
                onClick={() => navigate("/checkout/Address")}
              >
                Proceed to checkout
              </button>
            </div>
          )}
        </div>
        <Footer />
      </>
    );
  };

  export default Cart;